import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { AlertTriangle, ShieldAlert, Mail } from 'lucide-react';
import SEO from '@/components/seo/SEO';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { formatCurrency } from '@/lib/utils';
import { trackCalculatorUsage, trackSignupIntent } from '@/lib/tracking';
import { EmailResultsDialog } from '@/components/tools/EmailResultsDialog';

const FineCalculatorPage: React.FC = () => {
  const [durationMonths, setDurationMonths] = useState([6]);
  const [siteAcres, setSiteAcres] = useState([5]);
  const [inspectionRate, setInspectionRate] = useState([75]); 
  const [isEmailOpen, setIsEmailOpen] = useState(false);

  const maxDailyFine = 64618; 
  const inspectionsPerMonth = 4; 
  
  const totalRequiredInspections = durationMonths[0] * inspectionsPerMonth;
  const missedInspections = Math.round(totalRequiredInspections * ((100 - inspectionRate[0]) / 100));
  const potentialFine = missedInspections * maxDailyFine;
  const likelySettlement = potentialFine * 0.15; 

  const appSignUpUrl = import.meta.env.VITE_APP_URL 
    ? `${import.meta.env.VITE_APP_URL}/auth?mode=signup`
    : 'http://localhost:3000/auth?mode=signup';

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "EPA Fine Risk Calculator",
    "description": "Calculate potential financial exposure from missed stormwater inspections.",
    "applicationCategory": "BusinessApplication",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
  };

  useEffect(() => {
      const timer = setTimeout(() => {
          if (durationMonths[0] !== 6 || siteAcres[0] !== 5 || inspectionRate[0] !== 75) {
              trackCalculatorUsage('fine_risk_calculator', {
                  duration_months: durationMonths[0],
                  site_acres: siteAcres[0],
                  inspection_rate: inspectionRate[0],
                  potential_fine: potentialFine
              });
          }
      }, 2000); 

      return () => clearTimeout(timer);
  }, [durationMonths, siteAcres, inspectionRate, potentialFine]);

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-red-500/20">
      <SEO 
        title="EPA Fine Calculator - SWPPP Compliance Risk"
        description="Calculate your potential financial exposure from missed stormwater inspections. Free tool for construction managers."
        canonical="/tools/fine-calculator"
        jsonLd={jsonLd}
      />

      <div className="bg-muted/30 pt-32 pb-20 border-b border-border">
        <div className="container px-4">
           <div className="max-w-4xl mx-auto text-center">
               <div className="mb-8 flex justify-center">
                   <Breadcrumbs items={[
                       { label: 'Resources', href: '/' },
                       { label: 'Fine Calculator', href: '/tools/fine-calculator' }
                   ]} />
               </div>

               <div className="inline-flex items-center justify-center p-3 bg-red-100 text-red-600 rounded-full mb-6">
                 <ShieldAlert size={32} />
               </div>
               <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">EPA Fine Risk Calculator</h1>
               <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                 Missed inspections cost more than just time. Under the Clean Water Act, fines can reach <span className="font-bold text-foreground">$64,000+ per day</span> per violation.
               </p>
           </div>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-start mb-24">
            
            {/* Calculator Inputs */}
            <Card className="border-border shadow-sm h-full">
                <CardHeader>
                    <CardTitle>Project Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-10">
                    <div className="space-y-4">
                        <div className="flex justify-between text-sm font-medium">
                            <label className="text-muted-foreground">Project Duration (Months)</label>
                            <span className="font-bold text-foreground bg-secondary px-2 py-1 rounded">{durationMonths[0]} Mo</span>
                        </div>
                        <Slider value={durationMonths} onValueChange={setDurationMonths} min={1} max={36} step={1} />
                    </div>

                    <div className="space-y-4">
                        <div className="flex justify-between text-sm font-medium">
                            <label className="text-muted-foreground">Site Size (Acres)</label>
                            <span className="font-bold text-foreground bg-secondary px-2 py-1 rounded">{siteAcres[0]} Acres</span>
                        </div>
                        <Slider value={siteAcres} onValueChange={setSiteAcres} min={1} max={100} step={1} />
                    </div>

                    <div className="space-y-4">
                        <div className="flex justify-between text-sm font-medium">
                            <label className="text-muted-foreground">Inspection Completion Rate</label>
                            <span className={`font-bold px-2 py-1 rounded ${inspectionRate[0] < 90 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                {inspectionRate[0]}%
                            </span>
                        </div>
                        <p className="text-xs text-muted-foreground">Be honest: What % of weekly inspections actually get done and filed?</p>
                        <Slider value={inspectionRate} onValueChange={setInspectionRate} min={50} max={100} step={5} />
                    </div>
                </CardContent>
            </Card>

            {/* Results */}
            <div className="space-y-8">
               <div className="relative overflow-hidden rounded-3xl bg-slate-900 text-white p-8 md:p-12 shadow-2xl">
                   <div className="relative z-10">
                        <p className="text-red-400 font-bold uppercase tracking-wide text-sm mb-2 flex items-center gap-2">
                            <AlertTriangle size={16}/> Maximum Exposure
                        </p>
                        <div className="text-4xl md:text-5xl font-black mb-2 tracking-tight text-white">
                            {formatCurrency(potentialFine)}
                        </div>
                        <p className="text-sm text-slate-400 mb-8">
                            Based on {missedInspections} missed inspections at max penalty.
                        </p>
                        
                        <div className="p-4 bg-white/10 rounded-xl border border-white/10 mb-8">
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-sm font-medium">Likely Settlement Cost</span>
                                <span className="text-xl font-bold">{formatCurrency(likelySettlement)}</span>
                            </div>
                            <p className="text-xs text-slate-400">Assuming 15% settlement rate + legal fees.</p>
                        </div>

                        <div className="flex flex-col gap-3">
                            <Button size="lg" className="w-full bg-red-600 hover:bg-red-700 text-white border-none h-12" asChild>
                                <a href={appSignUpUrl} onClick={() => trackSignupIntent('fine_calculator_result')}>Eliminate This Risk - Start Free</a>
                            </Button>
                            <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10" onClick={() => setIsEmailOpen(true)}>
                                <Mail className="mr-2 h-4 w-4"/> Email Me This Estimate
                            </Button>
                        </div>
                   </div>
               </div>

               <div className="bg-muted/50 p-6 rounded-2xl border border-border">
                   <h3 className="font-bold text-lg mb-4">Protect your business:</h3>
                   <ul className="space-y-3 text-sm text-muted-foreground mb-6">
                       <li className="flex gap-2 items-start"><div className="h-1.5 w-1.5 rounded-full bg-green-500 mt-1.5 shrink-0"/> <span><strong>Automated Reminders:</strong> Never miss a weekly or rain-event inspection.</span></li>
                       <li className="flex gap-2 items-start"><div className="h-1.5 w-1.5 rounded-full bg-green-500 mt-1.5 shrink-0"/> <span><strong>Cloud Archive:</strong> Every report is saved instantly. No more lost binders.</span></li>
                   </ul>
               </div>
            </div>
        </div>

        {/* SEO CONTENT SECTION */}
        <div className="max-w-3xl mx-auto prose dark:prose-invert">
            <h2>Understanding SWPPP Fines</h2>
            <p>
                Clean Water Act penalties can be substantial and are adjusted periodically. Use this tool only for scenario planning, then verify the current statutory maximum and enforcement factors with EPA or qualified counsel.
            </p>
            <p>
                It is important to understand that a "violation" is not just a single event like a sediment discharge. Administrative failures are also violations.
            </p>
            
            <h3>What counts as a violation?</h3>
            <ul>
                <li><strong>Missing Inspections:</strong> If your permit requires weekly inspections and you miss one, that is a violation. If you miss it for 7 days, that can be counted as 7 days of non-compliance.</li>
                <li><strong>Late Reports:</strong> Completing an inspection report 3 days after the 24-hour post-storm window is a violation.</li>
                <li><strong>Incomplete Records:</strong> Reports without signatures or photos of corrective actions are often considered invalid by auditors.</li>
            </ul>

            <h3>How is the "Settlement Amount" calculated?</h3>
            <p>
                While the maximum fine is $64k/day, the EPA typically negotiates settlements based on the "Ben-Abel" model (Benefit of Non-Compliance + Gravity of Violation).
            </p>
            <p>
                However, legal fees, project delays, and "Stop Work Orders" often cost far more than the fine itself. This calculator estimates a "Likely Settlement" at 15% of the maximum exposure, which is a common baseline for initial settlement offers in administrative cases involving paperwork deficiencies.
            </p>

            <blockquote>
                "The best way to avoid fines is to remove the human element of remembering to inspect. Automation is your safety net."
            </blockquote>
        </div>
      </div>

      <EmailResultsDialog 
        isOpen={isEmailOpen}
        onOpenChange={setIsEmailOpen}
        toolName="Fine Risk Calculator"
        resultSummary={formatCurrency(potentialFine)}
        resultDetails={{
            durationMonths: durationMonths[0],
            siteAcres: siteAcres[0],
            inspectionRate: inspectionRate[0],
            missedInspections,
            maxExposure: potentialFine,
            likelySettlement
        }}
      />
    </div>
  );
};

export default FineCalculatorPage;
